<template>
  <!-- Redesigned nav rail (preview). Mirrors NavDrawer's behaviour with the
       "Queues Redesign" look: a flush dark-teal 64px column. -->
  <div
    v-if="!isCollapsed"
    class="rail shrink-0 flex flex-col items-center py-4 gap-4 self-stretch"
  >
    <div class="rail-logo">{{ brandInitial }}</div>

    <div class="flex flex-col items-center gap-1.5 flex-1 overflow-y-auto w-full">
      <template v-for="(navItem, i) in filteredNavItems" :key="i">
        <RouterLink
          v-if="!navItem.children"
          :to="navItem.path"
          custom
          v-slot="{ navigate }"
        >
          <button
            class="rail-item"
            :class="{ 'rail-item--active': isActive(navItem) }"
            :title="railTitle(navItem.title)"
            @click="navigate"
          >
            <span v-if="badgeFor(navItem)" class="rail-badge">
              {{ badgeFor(navItem) }}
            </span>
            <v-icon size="22" :color="isActive(navItem) ? '#ffffff' : '#9FC4BF'">
              {{ navItem.icon }}
            </v-icon>
          </button>
        </RouterLink>

        <BubbleMenu v-else position="end">
          <template #trigger>
            <button
              class="rail-item"
              :class="{ 'rail-item--active': isChildActive(navItem) }"
              :title="railTitle(navItem.title)"
            >
              <v-icon
                size="22"
                :color="isChildActive(navItem) ? '#ffffff' : '#9FC4BF'"
              >
                {{ navItem.icon }}
              </v-icon>
            </button>
          </template>
          <template #default>
            <div class="flex flex-col gap-1">
              <RouterLink
                v-for="child in navItem.children"
                :key="child.path"
                :to="child.path"
                class="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100"
                :class="
                  $route.path.includes(child.path)
                    ? 'text-primary-500'
                    : 'text-gray-700'
                "
              >
                <v-icon size="18">{{ child.icon }}</v-icon>
                {{ $t(`layouts.${child.title}`) }}
              </RouterLink>
            </div>
          </template>
        </BubbleMenu>
      </template>
    </div>

    <div class="flex flex-col items-center gap-2 mt-auto">
      <button
        class="rail-item"
        :title="currentLocal === 'en' ? 'العربية' : 'English'"
        @click="changeLocale(currentLocal === 'en' ? 'ar' : 'en')"
      >
        <v-icon size="20" color="#7FA6A1">mdi-earth</v-icon>
      </button>
      <button class="rail-item" :title="$t('layouts.logout')" @click="logoutUser">
        <v-icon size="20" color="#E88A83">mdi-power</v-icon>
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import Swal from "sweetalert2";
import BubbleMenu from "@/components/BubbleMenu.vue";

export default {
  name: "AppRailNew",
  components: { BubbleMenu },
  computed: {
    ...mapGetters([
      "navigationsMenu",
      "permissions",
      "notReadChatMessages",
      "notReadOmniMessages",
      "isCollapsed",
      "domain",
    ]),
    filteredNavItems() {
      return this.navigationsMenu
        .filter(
          (item) =>
            !item.hide &&
            this.userHasPermisson(this.permissions, item.permission)
        )
        .map((item) => {
          if (item.children) {
            const visibleChildren = item.children.filter(
              (child) =>
                !child.hide &&
                this.userHasPermisson(
                  this.permissions,
                  child.permission || item.permission
                )
            );
            if (visibleChildren.length > 0) {
              return { ...item, children: visibleChildren };
            }
            return null;
          }
          return item;
        })
        .filter((item) => item !== null);
    },
    brandInitial() {
      return (this.domain || "C").charAt(0).toUpperCase();
    },
    currentLocal() {
      return this.$vuetify.locale.current;
    },
    chatNotifications() {
      return this.notReadChatMessages?.length > 0
        ? this.notReadChatMessages.length.toString()
        : "";
    },
    omniNotifications() {
      return this.notReadOmniMessages?.length > 0
        ? this.notReadOmniMessages.length.toString()
        : "";
    },
  },
  methods: {
    ...mapActions(["logout"]),
    ...mapMutations(["setLang"]),
    isActive(navItem) {
      return this.$route.path.includes(navItem.path);
    },
    isChildActive(navItem) {
      if (!navItem.children) return false;
      return navItem.children.some((child) =>
        this.$route.path.includes(child.path)
      );
    },
    railTitle(title) {
      return this.$t(`layouts.${title}`);
    },
    badgeFor(navItem) {
      if (navItem.path.includes("/Chat") && this.chatNotifications) {
        return this.chatNotifications;
      }
      if (navItem.path.includes("/social-media") && this.omniNotifications) {
        return this.omniNotifications;
      }
      return "";
    },
    changeLocale(locale) {
      const html = document.documentElement;
      html.dir = locale === "ar" ? "rtl" : "ltr";
      this.$vuetify.locale.current = locale;
      this.setLang(locale);
    },
    logoutUser() {
      Swal.fire({
        title: this.$t("layouts.logout"),
        text: this.$t("layouts.logout_text"),
        icon: "warning",
        showCancelButton: true,
        customClass: {
          confirmButton: "!bg-primary-500 text-white",
          cancelButton: "!bg-gray-500 text-white",
        },
      }).then((result) => {
        if (result.isConfirmed) {
          this.logout();
        }
      });
    },
  },
};
</script>

<style scoped>
.rail {
  width: 64px;
  background: #0e3b3b;
}
.rail-logo {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: #1aa392;
  color: #fff;
  font-weight: 800;
  font-size: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.rail-item {
  position: relative;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  transition: background 0.15s ease;
}
.rail-item:hover {
  background: rgba(255, 255, 255, 0.08);
}
.rail-item--active {
  background: rgba(255, 255, 255, 0.14);
}
.rail-badge {
  position: absolute;
  top: 2px;
  inset-inline-end: 2px;
  z-index: 10;
  background: #ef4444;
  color: #fff;
  font-size: 10px;
  border-radius: 9999px;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
